import React, { useState, useEffect, useCallback } from 'react';
import { Users, Trash2, Lock } from 'lucide-react';
import { api, timeAgo } from '../api';
import { toast } from 'sonner';

const ROLES = ['pending', 'agent', 'admin', 'tv'];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => api.get('/users').then((r) => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`${u.name || u.emp_id} is now ${role === 'pending' ? 'awaiting approval' : `a ${role.toUpperCase()}`}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.emp_id} from SND CRM?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name || u.emp_id} removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove user');
    }
  };

  const pendingCount = users.filter((u) => u.role === 'pending').length;

  return (
    <div data-testid="admin-users">
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <h3 className="font-display font-bold text-lg text-slate-800 flex items-center gap-2"><Users size={17} className="text-blue-600" /> Team & Roles</h3>
        {pendingCount > 0 && (
          <span className="bg-violet-100 text-violet-700 border border-violet-300 rounded-full px-2.5 py-0.5 text-[11px] font-extrabold animate-pulse" data-testid="pending-users-badge">
            {pendingCount} waiting for a role
          </span>
        )}
        <p className="font-hand text-base text-slate-400">employees log in via emp portal · you decide who is Agent / Admin / TV</p>
      </div>
      <div className="bg-white border-2 border-slate-200 rounded-xl sticky-shadow overflow-hidden">
        {users.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.emp_id}`}
            className={`flex items-center gap-3 px-3 py-2.5 border-b border-slate-50 flex-wrap ${u.role === 'pending' ? 'bg-violet-50/60' : ''}`}>
            <div className="min-w-[160px]">
              <p className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                {u.name || u.emp_id}
                {u.is_primary && <span className="flex items-center gap-0.5 bg-amber-100 text-amber-700 border border-amber-300 rounded-full px-1.5 py-0.5 text-[8px] font-extrabold uppercase" title="Primary admin — cannot be removed"><Lock size={8} /> Primary</span>}
              </p>
              <p className="text-[10px] text-slate-400 font-semibold">ID {u.emp_id}{u.designation ? ` · ${u.designation}` : ''}{u.auth === 'local' ? ' · local account' : ''}</p>
            </div>
            <select
              data-testid={`user-role-select-${u.emp_id}`}
              value={u.role}
              disabled={u.is_primary}
              onChange={(e) => setRole(u, e.target.value)}
              className="border-2 border-slate-200 rounded-lg px-2 py-1 text-xs font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
              {ROLES.map((r) => <option key={r} value={r}>{r === 'pending' ? '⏳ pending' : r === 'agent' ? '📝 agent' : r === 'admin' ? '🛡 admin' : '📺 tv'}</option>)}
            </select>
            <span className="text-[10px] text-slate-400 font-semibold flex-1">last login {timeAgo(u.last_login)}</span>
            <button
              data-testid={`user-delete-${u.emp_id}`}
              onClick={() => remove(u)}
              disabled={u.is_primary}
              title={u.is_primary ? 'Primary admin cannot be removed' : 'Remove user'}
              className="p-1.5 rounded-lg text-slate-300 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:text-slate-300 disabled:hover:bg-transparent">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
